import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    title: '',
    department: '',
    bio: '',
    memberType: '组内成员（在读学生）', // 默认值
    verificationCode: '', // 新增验证码字段
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 发送验证码
  const handleSendVerificationCode = async () => {
    if (!formData.email) {
      setError('请先输入邮箱地址');
      return;
    }

    // 简单的邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    setSendingCode(true);
    setError('');

    try {
      const response = await apiService.sendEmailVerificationCode(formData.email);
      
      if (response.success) {
        setCodeSent(true);
        setError('');
        // 开始倒计时
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.message || '发送验证码失败');
      }
    } catch (err) {
      console.error('发送验证码错误:', err);
      setError('发送验证码失败，请稍后重试');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // 验证用户名长度
    if (formData.username.length < 2) {
      setError('用户名长度至少2个字符');
      setLoading(false);
      return;
    }

    // 验证密码
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('密码长度至少6位');
      setLoading(false);
      return;
    }

    // 验证邮箱验证码
    if (!formData.verificationCode) {
      setError('请输入邮箱验证码');
      setLoading(false);
      return;
    }

    try {
      // 先验证邮箱验证码
      const verifyResponse = await apiService.verifyEmailCode(formData.email, formData.verificationCode);
      
      if (!verifyResponse.success) {
        setError('邮箱验证码错误或已过期');
        setLoading(false);
        return;
      }

      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        profile: {
          name: formData.name,
          title: formData.title,
          department: formData.department,
          bio: formData.bio,
          avatar: '',
          memberType: formData.memberType // 必须放在profile里
        }
      };

      // 调试：打印发送的数据
      console.log('发送的注册数据:', userData);

      const success = await register(userData);
      if (success) {
        navigate('/');
      } else {
        setError('注册失败，请检查输入信息');
      }
    } catch (err) {
      console.error('注册错误详情:', err);
      setError('注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const memberTypeOptions = [
    { value: '组内教师', label: '组内教师', titles: ['研究员', '副研究员', '教授', '副教授', '讲师', '博士后'] },
    { value: '组内成员（在读学生）', label: '组内成员（在读学生）', titles: ['博士生', '硕士生', '本科生'] },
    { value: '组内成员（已毕业）', label: '组内成员（已毕业）', titles: ['博士生', '硕士生', '本科生'] },
    { value: '组外成员', label: '组外成员', titles: ['访问学者', '博士后'] },
  ];

  const currentType = memberTypeOptions.find(opt => opt.value === formData.memberType);
  const availableTitles = currentType ? currentType.titles : [];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">张</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          注册账户
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          或{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            登录现有账户
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6 register-form" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            {/* 新增说明 */}
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-md text-sm">
              <div>请选择您的成员类型和职称，注册后将自动出现在团队成员页面对应分组。</div>
              <div>如：<b>组内教师</b>对应“教师团队”，<b>组内成员（在读学生）</b>对应“在读学生”分组。</div>
            </div>

            {/* 1. 用户名 */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                用户名 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="请输入用户名（至少2个字符）"
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                用户名长度至少2个字符，用于登录。
              </div>
            </div>

            {/* 2. 邮箱地址 */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                邮箱地址 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 flex space-x-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field flex-1"
                  placeholder="请输入邮箱地址"
                />
                <button
                  type="button"
                  onClick={handleSendVerificationCode}
                  disabled={sendingCode || countdown > 0 || !formData.email}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingCode ? (
                    '发送中...'
                  ) : countdown > 0 ? (
                    `重新发送(${countdown}s)`
                  ) : (
                    '发送验证码'
                  )}
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                请输入邮箱地址后点击"发送验证码"按钮获取验证码。
              </div>
            </div>

            {/* 验证码输入框 - 在邮箱地址下方显示 */}
            {codeSent && (
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                  邮箱验证码 <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    autoComplete="one-time-code"
                    required
                    value={formData.verificationCode}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="请输入6位数字验证码"
                    maxLength={6}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  验证码已发送到您的邮箱，有效期5分钟。
                </div>
              </div>
            )}

            {/* 3. 姓名 */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                姓名 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="请输入真实姓名"
                />
              </div>
            </div>

            {/* 4. 成员类型 */}
            <div>
              <label htmlFor="memberType" className="block text-sm font-medium text-gray-700">
                成员类型 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <select
                  id="memberType"
                  name="memberType"
                  value={formData.memberType}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  {memberTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                说明：<b>组内教师</b>对应"教师团队"，<b>组内成员（在读学生）</b>对应"在读学生"分组，<b>组内成员（毕业学生）</b>对应"毕业学生"。
              </div>
            </div>

            {/* 5. 职称 */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                职称 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <select
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">请选择职称</option>
                  {availableTitles.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                说明：职称选项会根据成员类型自动变化。
              </div>
            </div>

            {/* 6. 所属部门（可选） */}
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                所属部门
              </label>
              <div className="mt-1">
                <input
                  id="department"
                  name="department"
                  type="text"
                  autoComplete="organization"
                  value={formData.department}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="请输入所属部门（可选）"
                />
              </div>
            </div>

            {/* 7. 个人简介 */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                个人简介
              </label>
              <div className="mt-1">
                <RichTextEditor
                  value={formData.bio}
                  onChange={(value) => setFormData({ ...formData, bio: value })}
                  placeholder="请简要介绍自己（可选）"
                  height={120}
                />
              </div>
            </div>

            {/* 8. 密码 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                密码 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field pr-10"
                  placeholder="请输入密码（至少6位）"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* 9. 确认密码 */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                确认密码 <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-field pr-10"
                  placeholder="请再次输入密码"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '注册中...' : '注册'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">或者</span>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  已有账户？{' '}
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    立即登录
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 